import { useEffect, useRef, useState } from "react";
import { createGfPost, deleteGfPost, updateGfPost } from "../../../../api/giftingClient";
import {
  PLATFORM_LABEL,
  REUSE_SCOPE_LABEL,
  REUSE_STATE_LABEL,
  fmtMD,
  fmtYmd,
  reuseScopeLabels,
  reuseState,
} from "../../../../lib/giftingRules";
import type { GfDealDetail, GfPlatform, GfPost, GfReuseState } from "../../../../types/gifting";
import { errMsg } from "../../gfUtil";
import { ConfirmModal } from "../../modals/ConfirmModal";
import { GfButton, GfField, GfTag, type GfTone } from "../../ui/GfUi";

const PLATFORMS = Object.keys(PLATFORM_LABEL) as GfPlatform[];
const POST_TYPES = ["リール", "フィード", "ストーリーズ", "動画", "ショート", "ブログ"];
const REUSE_USES = ["ad", "ec", "sns"] as const;
const REUSE_TONE: Record<GfReuseState, GfTone> = {
  granted: "ok",
  expiring: "caution",
  expired: "warn",
  requested: "info",
  none: "muted",
};

type ReuseStatus = "none" | "requested" | "granted";

/** 投稿の記録・確認(verified)・PR表記確認・2次利用の許諾（用途＋期限）・削除 */
export function PostsSection({
  detail,
  nowIso,
  onChanged,
  toast,
  focusSignal,
}: {
  detail: GfDealDetail;
  nowIso: string;
  onChanged: () => Promise<void>;
  toast: (m: string) => void;
  /** 値が変わるたびに追加フォームを開いてこのセクションへスクロールする（主ボタンから） */
  focusSignal: number;
}) {
  const rootRef = useRef<HTMLElement | null>(null);
  const [adding, setAdding] = useState(false);
  const [platform, setPlatform] = useState<GfPlatform>("instagram");
  const [url, setUrl] = useState("");
  const [postType, setPostType] = useState("");
  const [postedAt, setPostedAt] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [reuseStatus, setReuseStatus] = useState<ReuseStatus>("granted");
  const [uses, setUses] = useState<string[]>(["ad", "ec"]);
  const [until, setUntil] = useState("");
  const [removing, setRemoving] = useState<GfPost | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (focusSignal === 0) return;
    setAdding(true);
    rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusSignal]);

  const run = async (fn: () => Promise<unknown>, okMsg: string): Promise<boolean> => {
    setBusy(true);
    try {
      await fn();
      toast(okMsg);
      await onChanged();
      return true;
    } catch (e) {
      toast(errMsg(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    if (!/^https?:\/\//.test(url.trim())) return toast("投稿URLは http(s):// から入力してください");
    const ok = await run(
      () =>
        createGfPost({
          deal_id: detail.id,
          platform,
          post_url: url.trim(),
          post_type: postType.trim() || null,
          posted_at: postedAt || null,
        }),
      "投稿を追加しました"
    );
    if (ok) {
      setUrl("");
      setPostType("");
      setPostedAt("");
      setAdding(false);
    }
  };

  const openReuse = (p: GfPost) => {
    setEditing(p.id);
    setReuseStatus(p.reuse_allowed === 1 ? "granted" : p.reuse_requested_at ? "requested" : "granted");
    const cur = (p.reuse_scope ?? "").split(",").map((s) => s.trim()).filter(Boolean);
    setUses(cur.length ? cur : ["ad", "ec"]);
    setUntil(p.reuse_until ? fmtYmd(p.reuse_until) || p.reuse_until.slice(0, 10) : "");
  };

  const saveReuse = async (p: GfPost) => {
    if (reuseStatus === "granted" && uses.length === 0) return toast("用途を1つ以上選んでください");
    const input =
      reuseStatus === "granted"
        ? { reuse_allowed: 1 as const, reuse_scope: uses.join(","), reuse_until: until || null }
        : reuseStatus === "requested"
          ? { reuse_allowed: 0 as const, reuse_requested_at: nowIso, reuse_scope: uses.join(",") || null }
          : { reuse_allowed: 0 as const, reuse_requested_at: null, reuse_until: null, reuse_scope: null };
    const ok = await run(() => updateGfPost(p.id, input), "2次利用の状態を保存しました");
    if (ok) setEditing(null);
  };

  return (
    <section className="gf-panel-section" ref={rootRef}>
      <div className="gf-panel-section-head">
        <div className="gf-panel-section-title">投稿・2次利用</div>
        <button type="button" className="gf-link-btn" onClick={() => setAdding((v) => !v)}>
          {adding ? "閉じる" : "＋ 投稿を追加"}
        </button>
      </div>

      {detail.posts.length === 0 && !adding && <div className="gf-note">投稿はまだ記録されていません。</div>}

      {detail.posts.map((p) => {
        const rs = reuseState(p, nowIso);
        const scopes = reuseScopeLabels(p.reuse_scope);
        return (
          <div className="gf-post" key={p.id}>
            <div className="gf-post-row">
              {p.thumbnail_url && (
                <a href={p.post_url} target="_blank" rel="noreferrer noopener" className="gf-post-thumb" aria-label="投稿を開く">
                  <img src={p.thumbnail_url} alt="" loading="lazy" />
                </a>
              )}
              <div className="gf-post-main">
                <div className="gf-post-top">
                  <a href={p.post_url} target="_blank" rel="noreferrer noopener" className="gf-post-link">
                    {PLATFORM_LABEL[p.platform]}
                    {p.post_type ? ` ${p.post_type}` : ""}
                  </a>
                  <span className="gf-mono gf-post-date">{fmtMD(p.posted_at ?? p.created_at)}</span>
                </div>
                <div className="gf-post-tags">
                  <GfTag tone={p.verified ? "ok" : "muted"}>{p.verified ? "確認済み" : "未確認"}</GfTag>
                  <GfTag tone={p.pr_label_checked ? "ok" : "caution"}>{p.pr_label_checked ? "PR表記 確認済み" : "PR表記 未確認"}</GfTag>
                  <GfTag tone={REUSE_TONE[rs]}>
                    2次利用 {REUSE_STATE_LABEL[rs]}
                    {scopes.length && (rs === "granted" || rs === "expiring") ? `（${scopes.join("・")}）` : ""}
                  </GfTag>
                  {p.source !== "manual" && <GfTag tone="info">{p.source === "ig_api" ? "IG API で検知" : "本人報告"}</GfTag>}
                </div>
                {p.reuse_until && <div className="gf-note">許諾期限 {fmtYmd(p.reuse_until)}</div>}
              </div>
            </div>
            <div className="gf-post-actions">
              <button
                type="button"
                className="gf-link-btn"
                disabled={busy}
                onClick={() => void run(() => updateGfPost(p.id, { verified: p.verified ? 0 : 1 }), p.verified ? "確認を外しました" : "投稿を確認済みにしました")}
              >
                {p.verified ? "確認を外す" : "投稿を確認"}
              </button>
              <button
                type="button"
                className="gf-link-btn"
                disabled={busy}
                onClick={() =>
                  void run(() => updateGfPost(p.id, { pr_label_checked: p.pr_label_checked ? 0 : 1 }), p.pr_label_checked ? "PR表記の確認を外しました" : "PR表記を確認済みにしました")
                }
              >
                {p.pr_label_checked ? "PR確認を外す" : "PR表記を確認"}
              </button>
              <button type="button" className="gf-link-btn" onClick={() => (editing === p.id ? setEditing(null) : openReuse(p))}>
                2次利用
              </button>
              <button type="button" className="gf-link-btn is-danger" disabled={busy} onClick={() => setRemoving(p)}>
                削除
              </button>
            </div>
            {editing === p.id && (
              <div className="gf-boxed gf-reuse-edit">
                <div className="gf-field">
                  <span className="gf-field-label">状態</span>
                  <div className="gf-radios">
                    {(["granted", "requested", "none"] as ReuseStatus[]).map((v) => (
                      <label key={v} className="gf-check">
                        <input type="radio" name={`reuse-${p.id}`} checked={reuseStatus === v} onChange={() => setReuseStatus(v)} />
                        <span>{v === "granted" ? "許諾済み" : v === "requested" ? "申請中" : "未取得"}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {reuseStatus !== "none" && (
                  <div className="gf-field">
                    <span className="gf-field-label">用途</span>
                    <div className="gf-radios">
                      {REUSE_USES.map((u) => (
                        <label key={u} className="gf-check">
                          <input
                            type="checkbox"
                            checked={uses.includes(u)}
                            onChange={() => setUses((cur) => (cur.includes(u) ? cur.filter((x) => x !== u) : [...cur, u]))}
                          />
                          <span>{REUSE_SCOPE_LABEL[u]}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {reuseStatus === "granted" && (
                  <GfField label="許諾期限（空欄＝期限なし）">
                    <input className="gf-input is-mono" type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
                  </GfField>
                )}
                <div className="gf-actions">
                  <GfButton size="sm" variant="primary" disabled={busy} onClick={() => void saveReuse(p)}>
                    保存
                  </GfButton>
                  <GfButton size="sm" onClick={() => setEditing(null)}>
                    閉じる
                  </GfButton>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {adding && (
        <div className="gf-boxed gf-post-add">
          <div className="gf-form-grid">
            <GfField label="媒体">
              <select className="gf-select" value={platform} onChange={(e) => setPlatform(e.target.value as GfPlatform)}>
                {PLATFORMS.map((pl) => (
                  <option key={pl} value={pl}>
                    {PLATFORM_LABEL[pl]}
                  </option>
                ))}
              </select>
            </GfField>
            <GfField label="種別（任意）">
              <input className="gf-input" list="gf-post-types" value={postType} onChange={(e) => setPostType(e.target.value)} />
              <datalist id="gf-post-types">
                {POST_TYPES.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
            </GfField>
          </div>
          <GfField label="投稿URL">
            <input className="gf-input is-mono" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.instagram.com/reel/…" />
          </GfField>
          <GfField label="投稿日（任意）">
            <input className="gf-input is-mono" type="date" value={postedAt} onChange={(e) => setPostedAt(e.target.value)} />
          </GfField>
          <div className="gf-actions">
            <GfButton size="sm" variant="primary" disabled={busy || !url.trim()} onClick={() => void add()}>
              投稿を追加
            </GfButton>
            <GfButton size="sm" onClick={() => setAdding(false)}>
              やめる
            </GfButton>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!removing}
        title="投稿の記録を削除します"
        message={removing ? `${PLATFORM_LABEL[removing.platform]}の投稿記録を削除します。元の投稿は削除されません。` : ""}
        confirmLabel="削除する"
        danger
        busy={busy}
        onConfirm={() => {
          const p = removing;
          if (!p) return;
          void run(() => deleteGfPost(p.id), "投稿の記録を削除しました").then((ok) => ok && setRemoving(null));
        }}
        onClose={() => setRemoving(null)}
      />
    </section>
  );
}
